import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { socketManager } from 'utils/socketManager';
import { tradeApi } from 'store/services/trade/tradeApi';
import { profileApi } from 'store/services/profile/profileApi';
import { leaderboardApi } from 'store/services/leaderboard/leaderboardApi';
import { PointsUpdatePayload } from 'types/interfaces/points';
import type { AppDispatch } from 'store';

interface UseTraderBalanceSocketOptions {
  /** User IDs of traders visible on the current page */
  traderIds: number[];
  /** Whether the subscription is active */
  enabled?: boolean;
}

/**
 * Hook to subscribe to real-time point balance updates for traders
 * visible on Send Cards, Active Sending, and Active Receiving pages.
 *
 * Joins profile-${userId} rooms via profile:watch for each visible trader,
 * listens for profile:points_update events, and patches tradeApi caches
 * so displayed balances update immediately.
 */
export const useTraderBalanceSocket = ({
  traderIds,
  enabled = true,
}: UseTraderBalanceSocketOptions): void => {
  const dispatch = useDispatch<AppDispatch>();
  const hasAcquiredRef = useRef(false);
  const socketRef = useRef<ReturnType<typeof socketManager.acquireSocket> | null>(null);
  const watchedIdsRef = useRef<Set<number>>(new Set());

  // Serialize traderIds for stable effect dependency
  const traderIdsKey = traderIds.join(',');

  // Socket lifecycle: acquire/release socket, register event listener
  useEffect(() => {
    if (!enabled) return;

    hasAcquiredRef.current = true;
    const socket = socketManager.acquireSocket();
    socketRef.current = socket;

    const handleBalanceUpdate = (payload: PointsUpdatePayload) => {
      // Defer to a microtask: socket events can fire while a Redux reducer is
      // already executing (e.g. mid React commit), and chaining four dispatches
      // synchronously triggers "dispatch within reducer" errors in RTK Query.
      queueMicrotask(() => {
        for (const type of ['sending', 'receiving'] as const) {
          dispatch(
            tradeApi.util.updateQueryData('getActiveTrades', { type }, (draft: any) => {
              if (draft?.trades) {
                for (const trade of draft.trades) {
                  if (trade.traderId === payload.userId) {
                    trade.pointBalance = payload.newBalance;
                  }
                }
              }
            })
          );
        }

        dispatch(tradeApi.util.invalidateTags(['TradePackages']));
        dispatch(profileApi.util.invalidateTags(['PublicProfile']));
        dispatch(leaderboardApi.util.invalidateTags(['Leaderboard']));
      });
    };

    socket.on('profile:points_update', handleBalanceUpdate);

    return () => {
      // Unwatch all currently watched traders
      Array.from(watchedIdsRef.current).forEach(id => {
        socket.emit('profile:unwatch', { userId: id });
      });
      watchedIdsRef.current = new Set();

      socket.off('profile:points_update', handleBalanceUpdate);

      if (hasAcquiredRef.current) {
        socketManager.releaseSocket();
        hasAcquiredRef.current = false;
      }
      socketRef.current = null;
    };
  }, [enabled, dispatch]);

  // Room subscription management: watch/unwatch as traderIds change
  useEffect(() => {
    const socket = socketRef.current;
    if (!enabled || !socket) return;

    const newIds = new Set(traderIds);

    // Watch newly added traders (iterate the Set to avoid duplicate emissions)
    newIds.forEach(id => {
      if (!watchedIdsRef.current.has(id)) {
        socket.emit('profile:watch', { userId: id });
      }
    });

    // Unwatch removed traders
    Array.from(watchedIdsRef.current).forEach(id => {
      if (!newIds.has(id)) {
        socket.emit('profile:unwatch', { userId: id });
      }
    });

    watchedIdsRef.current = newIds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, traderIdsKey]);
};

export default useTraderBalanceSocket;
